import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePmoDeliveryView } from '../../../hooks/usePmoDelay.js';
import { ErrorState } from '../../feedback/ErrorState/index.js';
import { CauseBreakdownCard } from './CauseBreakdownCard.js';
import { DelayTrendCard } from './DelayTrendCard.js';
import { QuarterSelect } from './QuarterSelect.js';
import { ReleaseSummaryCard } from './ReleaseSummaryCard.js';
import { PMO_DELAY_CATEGORIES } from '../../../types/pmo-delay.js';

/**
 * デリバリー view。約束した期日に対して実際どう着地したかを、四半期ごとに見る画面。
 *
 * 右上のセレクトで四半期を切り替える。進行中の四半期は都度計算した最新値、終了した
 * 四半期は Q 終了時点で保存されたスナップショットが出る。元データ（遅延記録シート /
 * Backlog ミラー）は後から動くため、終了 Q を都度計算に倒すと当時の姿を再現できない。
 *
 * 上段は引き直し / 遅延の Q 内累積推移を横並びにする。2 つは同じ軸・同じ期間の対で、
 * 縦積みにすると片方を見るのにスクロールが要り比較にならない。
 * その下に原因別内訳、さらに下にリリース実績を置く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO KPI画面のデリバリー view。四半期セレクトで表示Qを切り替え、引き直し・遅延のQ内累積推移を横並びに置き、原因別内訳テーブルと当Q/先Qのリリース実績（100pt超え明細つき）を表示する
 * @graph-connects usePmoDeliveryView [calls] デリバリーview取得フック呼び出し
 * @graph-connects QuarterSelect [calls] 四半期セレクト表示
 * @graph-connects DelayTrendCard [calls] 区分別の遅延推移カード表示
 * @graph-connects CauseBreakdownCard [calls] 原因別内訳テーブル表示
 * @graph-connects ReleaseSummaryCard [calls] リリース実績カード表示
 * @graph-connects ErrorState [calls] エラー表示描画
 */
export function PmoDeliveryView() {
  const { t } = useTranslation();
  // 未選択は「進行中の四半期」。サーバーが選択肢と既定の Q を決めるので初期値は持たない
  const [quarter, setQuarter] = useState<string | undefined>(undefined);
  const { data, isLoading, error, refetch } = usePmoDeliveryView(quarter);

  const quarterLabel = data
    ? t('kpi.pmoDelivery.quarterLabel', {
        fiscalYear: data.delay.currentQuarter.fiscalYear,
        quarter: data.delay.currentQuarter.quarter,
      })
    : '';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-[var(--color-text-primary)]">
            {t('kpi.pmoDelivery.title')}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {t('kpi.pmoDelivery.description')}
          </p>
        </div>
        {data && (
          <QuarterSelect
            quarters={data.quarters}
            value={data.selectedQuarter}
            onChange={setQuarter}
          />
        )}
      </div>

      {data?.isSnapshot && (
        <p className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-text-secondary)]">
          {t('kpi.pmoDelivery.snapshotNote', { date: data.delay.asOfDate })}
        </p>
      )}

      {error && (
        <ErrorState
          title={t('feedback.errorTitle')}
          message={t('feedback.errorMessage')}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {isLoading && (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            {PMO_DELAY_CATEGORIES.map((category) => (
              <div
                key={category}
                className="h-96 animate-pulse rounded-lg bg-[var(--color-bg-elevated)]"
                data-testid="pmo-delay-skeleton"
              />
            ))}
          </div>
          <div
            className="h-96 animate-pulse rounded-lg bg-[var(--color-bg-elevated)]"
            data-testid="pmo-release-skeleton"
          />
        </>
      )}

      {data && (
        <>
          {/* 2 枚は対で読ませるので、広い画面では必ず同じ高さに並べる */}
          <div className="grid items-stretch gap-6 lg:grid-cols-2">
            {data.delay.series.map((series) => (
              <DelayTrendCard
                key={series.category}
                series={series}
                currentQuarter={data.delay.currentQuarter}
                previousQuarter={data.delay.previousQuarter}
                asOfDate={data.delay.asOfDate}
              />
            ))}
          </div>

          <CauseBreakdownCard rows={data.delay.causeBreakdown} quarterLabel={quarterLabel} />

          {data.delay.excludedRowCount > 0 && (
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('kpi.pmoDelivery.excludedNote', { count: data.delay.excludedRowCount })}
            </p>
          )}

          <ReleaseSummaryCard current={data.release.current} previous={data.release.previous} />
        </>
      )}
    </div>
  );
}
